import { Component, DestroyRef, Input, OnInit } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { Address3HelperService, AddressFormGroup, IrishCounty } from '../address3-helper.service';
import { CustomValidators, regexValidators } from '@shared/customValidators';
import { auditTime, distinctUntilChanged, filter, first, map, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { NGXLogger } from 'ngx-logger';

@Component({
    selector: 'address3-ireland',
    templateUrl: './address3-ireland.component.html'
})
export class Address3IrelandComponent implements OnInit {

    @Input()
    readonly = false;

    @Input()
    addressFormGroup: FormGroup<AddressFormGroup>;

    irishCountiesFiltered: Array<IrishCounty> = [];
    irishCountiesTypeahead = new Subject<string>();

    constructor(
        private readonly logger: NGXLogger,
        private readonly destroyRef: DestroyRef,
        private readonly address3Helper: Address3HelperService) {

        this.irishCountiesFiltered = this.address3Helper.getIrishCounties();

        this.irishCountiesTypeahead.pipe(
            takeUntilDestroyed(this.destroyRef),
            auditTime(300),
            distinctUntilChanged(),
            tap(() => {
                this.irishCountiesFiltered = this.address3Helper.getIrishCounties();
            }),
            filter(term => term?.length > 0),
            map(term => term.toLowerCase())
        ).subscribe(term => {
            const allCounties = this.address3Helper.getIrishCounties();
            const startsWith = allCounties.filter(county => county.name.toLowerCase().startsWith(term));
            const contains = allCounties.filter(county => county.name.toLowerCase().includes(term));
            this.irishCountiesFiltered = [startsWith, contains].find(a => a.length > 0) ?? [];
        });
    }

    get selectedCountry(): string {
        return this.addressFormGroup?.controls?.countryISO?.value;
    }

    get showingSearchResult(): boolean {
        return Boolean(this.addressFormGroup.controls.searchRefCode?.value);
    }

    ngOnInit(): void {
        this.address3Helper.resetValidators(this.addressFormGroup);
        this.initValidators();
        this.address3Helper.updateValidity(this.addressFormGroup);

        if (this.showingSearchResult) {
            this.invalidateSearchResultOnFirstEdit();
        }
    }

    private initValidators() {

        // line 1
        this.addressFormGroup.controls.line1.setValidators([
            Validators.required,
            Validators.maxLength(CustomValidators.getAddressMaxlength("line1"))
        ]);

        // line 2
        this.addressFormGroup.controls.line2.setValidators([
            Validators.maxLength(CustomValidators.getAddressMaxlength("line2"))
        ]);

        this.addressFormGroup.controls.city.setValidators([
            Validators.required,
            Validators.maxLength(CustomValidators.getAddressMaxlength("city"))
        ]);

        this.addressFormGroup.controls.province.setValidators([
            Validators.required
        ]);

        this.addressFormGroup.controls.postalCode.setValidators([
            Validators.pattern(regexValidators.postcodesByCountry.IRL),
            Validators.maxLength(CustomValidators.getAddressMaxlength("postalCode"))
        ]);
    }

    mapSearchResult(place: google.maps.places.PlaceResult) {
        const route = this.address3Helper.getGooglePlaceComponent(place, "route");
        const streetNumber = this.address3Helper.getGooglePlaceComponent(place, "street_number");
        const locality = this.address3Helper.getGooglePlaceComponent(place, "locality");
        const level2 = this.address3Helper.getGooglePlaceComponent(place, "administrative_area_level_2");
        const level1 = this.address3Helper.getGooglePlaceComponent(place, "administrative_area_level_1");
        const postalCode = this.address3Helper.getGooglePlaceComponent(place, "postal_code");

        const line1 = route
            ? [route, streetNumber].filter(Boolean).join(" ")
            : (place.formatted_address?.split(",")[0].trim() ?? "");

        const province = level1.replace(/^county\s+/i, '');

        this.addressFormGroup.patchValue({
            line1,
            line2: "",
            city: locality || level2,
            province: province,
            postalCode,
            searchRefCode: place.place_id
        });
        this.invalidateSearchResultOnFirstEdit();
    }

    invalidateSearchResultOnFirstEdit() {
        this.logger.debug("Manually edited, invalidating search results");
        this.addressFormGroup.valueChanges.pipe(
            takeUntilDestroyed(this.destroyRef),
            first()
        ).subscribe(() => {
            this.addressFormGroup.controls.searchRefCode.setValue("");
        });
    }

}
